
import { ReactRootManager } from './renderer/ReactRootManager';
import { LogoManager } from './renderer/LogoManager';
import { RenderRetryManager } from './renderer/RenderRetryManager';
import { BaseUrlContext } from './renderer/contexts';
import type { UrlResolver } from './UrlResolver';

// Re-export the BaseUrlContext so components can import it from this file
export { BaseUrlContext } from './renderer/contexts';

export class ReactRenderer {
  private container: HTMLElement;
  private urlResolver: UrlResolver;
  private rootManager: ReactRootManager;
  private logoManager: LogoManager;
  private retryManager: RenderRetryManager;
  private calculatorContainer: HTMLElement | null = null;
  
  constructor(container: HTMLElement, urlResolver: UrlResolver) {
    this.container = container;
    this.urlResolver = urlResolver;
    this.rootManager = new ReactRootManager();
    this.logoManager = new LogoManager(container, urlResolver);
    this.retryManager = new RenderRetryManager();
    console.log('[DEBUG] ReactRenderer initialized with container:', container);
    console.log('[DEBUG] Container is connected to DOM:', container.isConnected);
  }
  
  render(): void {
    try {
      console.log('[DEBUG] ReactRenderer.render() called');
      console.log('[DEBUG] Container state:', {
        exists: !!this.container,
        isConnected: this.container?.isConnected,
        innerHTML: this.container?.innerHTML?.substring(0, 50) + '...',
        childNodes: this.container?.childNodes?.length
      });
      
      this.cleanup();
      
      if (this.retryManager.hasExceededMaxAttempts()) {
        console.error(`[ERROR] Failed to render React component after ${this.retryManager.attempts} attempts`);
        this.showFallbackContent();
        return;
      }
      
      this.retryManager.incrementAttempts();
      console.log(`[DEBUG] Render attempt ${this.retryManager.attempts}`);
      
      if (!this.container) {
        console.error('[ERROR] Container is null, cannot render React');
        return;
      }
      
      this.setupCalculatorContainer();
      
      if (!this.calculatorContainer?.isConnected) {
        console.error('[ERROR] Calculator container is not connected to the DOM after appending');
        this.retryManager.scheduleRetry(() => this.render(), 500);
        return;
      }
      
      const baseUrl = this.urlResolver.getBaseUrl();
      console.log('[DEBUG] ReactRenderer using baseUrl:', baseUrl);
      
      // Add a class to help with CSS scoping
      if (this.container.parentElement) {
        this.container.parentElement.classList.add('onexip-calculator-scope');
      }
      
      setTimeout(() => {
        try {
          if (!this.calculatorContainer || !this.calculatorContainer.isConnected) {
            throw new Error('Calculator container is not connected to the DOM');
          }
          
          this.rootManager.createRoot(this.calculatorContainer);
          this.rootManager.renderApp(baseUrl);
          console.log('[DEBUG] React component rendered successfully');
          this.retryManager.resetAttempts();
          
          // Force CSS to be applied properly
          this.ensureStylesApplied();
        } catch (error) {
          console.error('[ERROR] Error creating React root:', error);
          console.error('[ERROR] Error stack:', error.stack);
          if (!this.retryManager.hasExceededMaxAttempts()) {
            this.retryManager.scheduleRetry(() => this.render(), 1000);
          } else {
            this.showFallbackContent();
          }
        }
      }, 300);
    } catch (error) {
      console.error('[ERROR] Fatal error in ReactRenderer:', error);
      this.showFallbackContent();
    }
  }
  
  private setupCalculatorContainer(): void {
    this.calculatorContainer = document.createElement('div');
    this.calculatorContainer.className = 'onexip-calculator-wrapper';
    this.calculatorContainer.style.cssText = 'display:block;width:100%;';
    console.log('[DEBUG] Created calculator container:', this.calculatorContainer);
    
    this.container.innerHTML = '';
    this.container.appendChild(this.calculatorContainer);
    console.log('[DEBUG] Appended calculator container to main container');
  }
  
  private ensureStylesApplied(): void {
    // Force a reflow to ensure styles are applied correctly in WordPress
    if (this.calculatorContainer) {
      console.log('[DEBUG] Forcing style reapplication');
      const height = this.calculatorContainer.offsetHeight;
      this.calculatorContainer.style.display = 'none';
      setTimeout(() => {
        if (this.calculatorContainer) {
          this.calculatorContainer.style.display = 'block';
        }
      }, 10);
    }
  }
  
  cleanup(): void {
    try {
      console.log('[DEBUG] ReactRenderer.cleanup() called');
      this.retryManager.cleanup();
      this.rootManager.cleanup();
      
      if (this.calculatorContainer && this.calculatorContainer.parentNode) {
        this.calculatorContainer.parentNode.removeChild(this.calculatorContainer);
        this.calculatorContainer = null;
      }
    } catch (error) {
      console.error('[ERROR] Error cleaning up ReactRenderer:', error);
    }
  }
  
  addLogo(): void {
    this.logoManager.addLogo();
  }
  
  private showFallbackContent(): void {
    try {
      console.log('[DEBUG] Showing fallback content due to React rendering failure');
      if (this.container) {
        this.container.innerHTML = `
          <div style="padding: 20px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            <h2 style="color: #333; margin-bottom: 1rem;">Cost Calculator</h2>
            <p style="color: #666;">There was an error loading the calculator. Please refresh the page or contact support.</p>
            <p style="color: #666; font-size: 0.8rem; margin-top: 1rem;">Error details: Failed to render component correctly. Debug information has been logged to console.</p>
          </div>
        `;
        console.log('[DEBUG] Fallback content displayed');
      } else {
        console.error('[ERROR] Cannot show fallback content: container is null');
      }
    } catch (error) {
      console.error('[ERROR] Error showing fallback content:', error);
    }
  }
}
